import React, { useMemo } from "react";
import {
    Badge,
    Button,
    Card,
    Col,
    Empty,
    List,
    Progress,
    Row,
    Space,
    Statistic,
    Tag,
    Typography,
} from "antd";
import {
    ArrowRightOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    FundOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    formatAmount,
    formatDateTime,
    getParentStatusColor,
    getParentStatusLabel,
} from "./transactionScheduleUi";
import { buildTransactionScheduleSummary } from "./transactionScheduleSummary";

const { Text } = Typography;

function TransactionScheduleDashboard({
    transactions = [],
    returns = [],
    onOpenRecord,
    onGoSchedules,
    onQuickFilter,
}) {
    const summary = useMemo(
        () => buildTransactionScheduleSummary(transactions),
        [transactions],
    );

    const recentRows = useMemo(() => {
        return [...transactions]
            .sort((a, b) => {
                const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return bTime - aTime;
            })
            .slice(0, 6);
    }, [transactions]);

    const attentionRows = useMemo(() => {
        return transactions
            .filter((item) => {
                const total = Number(item.allocationCount || 0);
                const agreed = Number(item.agreedAllocationCount || 0);

                return (
                    item.parentDisplayStatus === "EMPTY" ||
                    (total > 0 && agreed < total) ||
                    Number(item.reviewAllocationCount || 0) > 0 ||
                    Number(item.returnedAllocationCount || 0) > 0
                );
            })
            .slice(0, 6);
    }, [transactions]);

    const agreementBase = summary.fullyAgreedCount + summary.waitingAgreeCount;
    const agreementPercent =
        agreementBase > 0
            ? Math.round((summary.fullyAgreedCount / agreementBase) * 100)
            : 0;
    function getScheduleStateStyle(record) {
        const total = Number(record.allocationCount || 0);
        const agreed = Number(record.agreedAllocationCount || 0);
        const returned = Number(record.returnedAllocationCount || 0);
        const review = Number(record.reviewAllocationCount || 0);
        const verified = Number(record.verifiedAllocationCount || 0);

        if (total > 0 && agreed < total) {
            return { background: "#fff7e6", border: "1px solid #ffd591" };
        }
        if (returned > 0) {
            return { background: "#fff1f0", border: "1px solid #ffa39e" };
        }
        if (review > 0) {
            return { background: "#fffbe6", border: "1px solid #ffe58f" };
        }
        if (record.parentDisplayStatus === "EMPTY") {
            return { background: "#fafafa", border: "1px solid #d9d9d9" };
        }
        if (verified > 0) {
            return { background: "#f6ffed", border: "1px solid #b7eb8f" };
        }

        return {};
    }


    return (
        <div className="space-y-4">
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} xl={6}>
                    <Card bordered={false} className="shadow-sm rounded-2xl">
                        <Statistic
                            title="Нийт хүсэлт"
                            value={summary.totalCount}
                            prefix={<FundOutlined />}
                        />
                        <Text type="secondary">
                            Нийт дүн: {formatAmount(summary.totalAmount)}
                        </Text>
                    </Card>
                </Col>

                <Col xs={24} sm={12} xl={6}>
                    <Card
                        bordered={false}
                        className="shadow-sm rounded-2xl cursor-pointer"
                        onClick={() => onQuickFilter?.("EMPTY")}
                    >
                        <Statistic
                            title="Хоосон хүсэлт"
                            value={summary.emptyCount}
                            prefix={<WarningOutlined />}
                        />
                        <Text type="secondary">
                            Хуваарилалт үүсээгүй хүсэлтүүд
                        </Text>
                    </Card>
                </Col>

                <Col xs={24} sm={12} xl={6}>
                    <Card
                        bordered={false}
                        className="shadow-sm rounded-2xl cursor-pointer"
                        onClick={() => onQuickFilter?.("WAITING")}
                    >
                        <Statistic
                            title="Зөвшөөрөл хүлээж буй"
                            value={summary.waitingAgreeCount}
                            prefix={<ClockCircleOutlined />}
                        />
                        <Text type="secondary">
                            Бүрэн зөвшөөрөгдөөгүй мөрүүд
                        </Text>
                    </Card>
                </Col>

                <Col xs={24} sm={12} xl={6}>
                    <Card
                        bordered={false}
                        className={`shadow-sm rounded-2xl cursor-pointer ${returns.length > 0 ? "ts-pulse" : ""}`}
                        onClick={() => onQuickFilter?.("RETURNS")}
                        style={{
                            border: returns.length > 0 ? "1px solid #ffccc7" : undefined,
                            background: returns.length > 0 ? "#fff2f0" : undefined,
                        }}
                    >
                        <Statistic
                            title="Буцаалт"
                            value={returns.length}
                            prefix={<CheckCircleOutlined style={{ color: returns.length > 0 ? "#cf1322" : undefined }} />}
                            valueStyle={returns.length > 0 ? { color: "#cf1322" } : undefined}
                        />
                        <Text type="secondary">
                            Шалгах шаардлагатай буцаалтууд
                        </Text>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} xl={10}>
                    <Card
                        bordered={false}
                        className="shadow-sm rounded-2xl"
                        title="Ерөнхий явц"
                        extra={
                            <Button type="link" onClick={onGoSchedules}>
                                Ажлын дэлгэц рүү <ArrowRightOutlined />
                            </Button>
                        }
                    >
                        <Progress percent={agreementPercent} status="active" />
                        <Space wrap size={[8, 8]} style={{ marginTop: 16 }}>
                            <Tag color="default">Хоосон: {summary.emptyCount}</Tag>
                            <Tag color="blue">Нэг: {summary.singleCount}</Tag>
                            <Tag color="orange">Хэсэгчилсэн: {summary.partialCount}</Tag>
                            <Tag color="green">Бүрэн зөвшөөрөгдсөн: {summary.fullyAgreedCount}</Tag>
                        </Space>

                        <div style={{ marginTop: 16 }}>
                            <Badge color="#fa8c16" text={`Гараар шалгах: ${summary.reviewCount}`} />
                            <br />
                            <Badge color="#cf1322" text={`Буцаалт: ${summary.returnedCount}`} />
                            <br />
                            <Badge color="#1677ff" text={`Эцэслэн баталгаажсан: ${summary.verifiedCount}`} />
                        </div>
                    </Card>
                </Col>

                <Col xs={24} xl={14}>
                    <Card bordered={false} className="shadow-sm rounded-2xl" title="Анхаарал шаардлагатай хүсэлтүүд">
                        {attentionRows.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Анхаарах хүсэлт алга" />
                        ) : (
                            <List
                                dataSource={attentionRows}
                                renderItem={(item) => (
                                    <List.Item
                                        style={{
                                            ...getScheduleStateStyle(item),
                                            borderRadius: 10,
                                            paddingLeft: 12,
                                            paddingRight: 12,
                                            marginBottom: 8,
                                        }}
                                        actions={[
                                            <Button type="link" onClick={() => onOpenRecord?.(item)}>
                                                Дэлгэрэнгүй
                                            </Button>,
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={
                                                <Space wrap>
                                                    <Text strong>{item.requestNumber || `#${item.id}`}</Text>
                                                    <Tag color={getParentStatusColor(item.parentDisplayStatus)}>
                                                        {getParentStatusLabel(item.parentDisplayStatus)}
                                                    </Tag>
                                                </Space>
                                            }
                                            description={
                                                <Space direction="vertical" size={2}>
                                                    <Text type="secondary">{item.payer || "-"}</Text>
                                                    <Text type="secondary">
                                                        {formatAmount(item.paymentAmount)} {item.currency || ""}
                                                    </Text>
                                                    <Text type="secondary">{formatDateTime(item.createdAt)}</Text>
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        )}
                    </Card>
                </Col>

                <Col xs={24}>
                    <Card bordered={false} className="shadow-sm rounded-2xl" title="Сүүлд нэмэгдсэн хүсэлтүүд">
                        {recentRows.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Өгөгдөл алга" />
                        ) : (
                            <List
                                grid={{ gutter: 16, xs: 1, md: 2, xl: 3 }}
                                dataSource={recentRows}
                                renderItem={(item) => (
                                    <List.Item>
                                        <Card
                                            hoverable
                                            className="rounded-2xl"
                                            bodyStyle={{ padding: 16 }}
                                            onClick={() => onOpenRecord?.(item)}
                                        >
                                            <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                                <Space wrap>
                                                    <Text strong>{item.requestNumber || `#${item.id}`}</Text>
                                                    <Tag color={getParentStatusColor(item.parentDisplayStatus)}>
                                                        {getParentStatusLabel(item.parentDisplayStatus)}
                                                    </Tag>
                                                </Space>
                                                <Text ellipsis>{item.payer || "-"}</Text>
                                                <Text>{formatAmount(item.paymentAmount)} {item.currency || ""}</Text>
                                                <Text type="secondary">{formatDateTime(item.createdAt)}</Text>
                                            </Space>
                                        </Card>
                                    </List.Item>
                                )}
                            />
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default TransactionScheduleDashboard;